import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FileText, CheckCircle2, XCircle, RefreshCw, Search, ChevronLeft,
  AlertCircle, Send, Loader2, Upload, Inbox, ArrowDownToLine, ArrowUpFromLine
} from "lucide-react";
import { fmtUK } from "../lib/dateUtils";
import { toast } from "sonner";
import MatterSearchUnavailableNotice from "../components/MatterSearchUnavailableNotice";
// Aliased: OutgoingPostDetail already has a local searchMatters callback, and an
// import that shadows or is shadowed by a local name is how you get silent
// recursion. The alias makes the call site unambiguous in every file.
import { searchMatters as searchMattersApi, MatterSearchUnavailable, MATTER_SEARCH_UNAVAILABLE_MESSAGE } from "../lib/matterSearch";

const OUTGOING_DOC_TYPES = [
  { value: "letter", label: "Letter" },
  { value: "report", label: "Report" },
  { value: "contract", label: "Contract" },
  { value: "deed", label: "Deed" },
  { value: "completion_statement", label: "Completion Statement" },
  { value: "other", label: "Other" },
];

function StatusPill({ status }) {
  if (status === "filed" || status === "done")
    return (
      <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
        <CheckCircle2 className="w-3 h-3" />Filed
      </Badge>
    );
  if (status === "partial")
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
        <AlertCircle className="w-3 h-3" />Claimed, not filed
      </Badge>
    );
  if (status === "rejected")
    return (
      <Badge variant="outline" className="bg-white text-slate-500 border-slate-300 gap-1">
        <XCircle className="w-3 h-3" />Rejected
      </Badge>
    );
  return (
    <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
      <AlertCircle className="w-3 h-3" />Pending
    </Badge>
  );
}

// BM-H4537 (Vanessa, 20 Oct 2026): claiming files the letter, so the
// incoming tabs read Pending / Claimed, not filed / Claimed & filed.
const IN_TAB_LABELS = {
  pending: "Pending",
  partial: "Claimed, not filed",
  filed: "Claimed & filed",
};

export default function PostHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("incoming");

  // ── INCOMING (post-intake) state ──────────────────────────────────────────
  const [inItems, setInItems] = useState([]);
  const [inLoading, setInLoading] = useState(false);
  const [inCounts, setInCounts] = useState({ pending: 0, filed: 0, partial: 0 });
  const [inSyncing, setInSyncing] = useState(false);
  const [inSearch, setInSearch] = useState("");
  const [inStatusFilter, setInStatusFilter] = useState("pending");

  // ── OUTGOING state ────────────────────────────────────────────────────────
  const [outItems, setOutItems] = useState([]);
  const [outLoading, setOutLoading] = useState(false);
  const [outCounts, setOutCounts] = useState({ pending: 0, filed: 0 });
  const [outSyncing, setOutSyncing] = useState(false);
  const [outSearch, setOutSearch] = useState("");
  const [outStatusFilter, setOutStatusFilter] = useState("pending");

  // inline assign for outgoing
  const [assigningId, setAssigningId] = useState(null);
  const [assignMatterSearch, setAssignMatterSearch] = useState("");
  const [assignMatterResults, setAssignMatterResults] = useState([]);
  const [assignSearching, setAssignSearching] = useState(false);
  // BM-H285: an unreadable matter list gets its own state, so the UI can say
  // "we could not look" rather than showing an empty dropdown.
  const [assignMatterError, setAssignMatterError] = useState("");
  const assignSearchTimeout = useRef(null);

  // manual upload form (outgoing)
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [outMatterSearch, setOutMatterSearch] = useState("");
  const [outMatterResults, setOutMatterResults] = useState([]);
  const [outMatterRef, setOutMatterRef] = useState("");
  const [outDocType, setOutDocType] = useState("");
  const [outDescription, setOutDescription] = useState("");
  const [outDateSent, setOutDateSent] = useState(new Date().toISOString().slice(0, 10));
  const [outFile, setOutFile] = useState(null);
  const [outUploading, setOutUploading] = useState(false);
  const [outMatterSearching, setOutMatterSearching] = useState(false);
  const [outMatterError, setOutMatterError] = useState("");
  const fileInputRef = useRef(null);
  const matterSearchTimeout = useRef(null);
  const [outRecentItems, setOutRecentItems] = useState([]);

  // ── INCOMING fetch ────────────────────────────────────────────────────────
  const fetchIncoming = useCallback(async () => {
    setInLoading(true);
    try {
      const res = await axios.get(`${API}/postintake/queue?limit=50`);
      // Map queue response fields to the shape the UI expects
      const items = (res.data.items || []).map(item => ({
        ...item,
        matter_ref: item.assigned_matter_ref || item.matter_ref || "",
        created_at: item.received_at || item.created_at,
        // Map backend statuses to UI statuses: claimed/parked → partial, approved → filed
        status: item.status === "approved" ? "filed"
              : (item.status === "claimed" || item.status === "parked") ? "partial"
              // BM-H4537: rejected is not "Claimed & filed"; it shows under All.
              : item.status,
      }));
      setInItems(items);
      const pending = items.filter(i => i.status === "pending").length;
      const filed = items.filter(i => i.status === "filed").length;
      const partial = items.filter(i => i.status === "partial").length;
      setInCounts({ pending, filed, partial });
    } catch {
      toast.error("Failed to load incoming post");
    } finally {
      setInLoading(false);
    }
  }, []);

  // ── OUTGOING fetch ────────────────────────────────────────────────────────
  const fetchOutgoing = useCallback(async () => {
    setOutLoading(true);
    try {
      const res = await axios.get(`${API}/postintake/outgoing/scanned?limit=50`);
      setOutItems(res.data.items || []);
      setOutCounts({ pending: res.data.pending || 0, filed: res.data.filed || 0 });
    } catch {
      toast.error("Failed to load outgoing post");
    } finally {
      setOutLoading(false);
    }
  }, []);

  const fetchOutgoingRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/postintake/outgoing?limit=20`);
      setOutRecentItems(res.data.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchIncoming();
    fetchOutgoing();
    fetchOutgoingRecent();
  }, [fetchIncoming, fetchOutgoing, fetchOutgoingRecent]);

  // ── INCOMING sync ─────────────────────────────────────────────────────────
  const handleInSync = async () => {
    setInSyncing(true);
    try {
      const res = await axios.post(`${API}/postintake/sync`);
      toast.success(`Incoming sync complete — ${res.data.new_documents || res.data.new_items || 0} new items`);
      fetchIncoming();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to sync incoming post"));
    } finally {
      setInSyncing(false);
    }
  };

  // ── OUTGOING sync ─────────────────────────────────────────────────────────
  const handleOutSync = async () => {
    setOutSyncing(true);
    try {
      const res = await axios.post(`${API}/postintake/outgoing/sync`);
      toast.success(`Outgoing sync complete — ${res.data.pending} pending, ${res.data.filed} filed`);
      fetchOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to sync outgoing post"));
    } finally {
      setOutSyncing(false);
    }
  };

  // ── Assign outgoing to matter ─────────────────────────────────────────────
  const handleAssignMatterSearch = (query) => {
    setAssignMatterSearch(query);
    if (assignSearchTimeout.current) clearTimeout(assignSearchTimeout.current);
    if (!query.trim()) { setAssignMatterResults([]); return; }
    assignSearchTimeout.current = setTimeout(async () => {
      setAssignSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setAssignMatterError("");
        setAssignMatterResults(results);
      } catch (err) {
        setAssignMatterResults([]);
        setAssignMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setAssignSearching(false); }
    }, 300);
  };

  const handleAssignToMatter = async (itemId, matterRef) => {
    try {
      await axios.post(`${API}/postintake/outgoing/${itemId}/assign`, { matter_ref: matterRef, assigned_by: "" });
      toast.success(`Filed to ${matterRef}`);
      setAssigningId(null);
      setAssignMatterSearch("");
      setAssignMatterResults([]);
      fetchOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to assign to matter"));
    }
  };

  // ── Manual upload (outgoing) ───────────────────────────────────────────────
  const handleMatterSearch = (query) => {
    setOutMatterSearch(query);
    setOutMatterRef("");
    if (matterSearchTimeout.current) clearTimeout(matterSearchTimeout.current);
    if (!query.trim()) { setOutMatterResults([]); return; }
    matterSearchTimeout.current = setTimeout(async () => {
      setOutMatterSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setOutMatterError("");
        setOutMatterResults(results);
      } catch (err) {
        setOutMatterResults([]);
        setOutMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setOutMatterSearching(false); }
    }, 300);
  };

  const selectMatter = (folder) => {
    setOutMatterRef(folder.name);
    setOutMatterSearch(folder.name);
    setOutMatterResults([]);
  };

  const handleOutgoingSubmit = async () => {
    const matterRef = outMatterRef || outMatterSearch.trim();
    if (!matterRef) { toast.error("Please select or enter a matter reference"); return; }
    if (!outDocType) { toast.error("Please select a document type"); return; }
    if (!outFile) { toast.error("Please select a file to upload"); return; }
    setOutUploading(true);
    try {
      const formData = new FormData();
      formData.append("matter_ref", matterRef);
      formData.append("document_type", outDocType);
      formData.append("date_sent", outDateSent);
      formData.append("description", outDescription.trim());
      formData.append("file", outFile);
      formData.append("uploaded_by", "");
      const res = await axios.post(`${API}/postintake/outgoing`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.sharepoint_url ? "Uploaded to SharePoint" : "Document recorded (SharePoint pending)");
      setOutMatterSearch(""); setOutMatterRef(""); setOutDocType("");
      setOutDescription(""); setOutDateSent(new Date().toISOString().slice(0, 10));
      setOutFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchOutgoingRecent();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to upload document"));
    } finally {
      setOutUploading(false);
    }
  };

  const fmt = (d) => fmtUK(d, "dd MMM yyyy HH:mm");

  // ── Filtered lists ────────────────────────────────────────────────────────
  const filteredIn = inItems.filter(item => {
    const matchStatus = inStatusFilter === "all" || item.status === inStatusFilter;
    if (!inSearch) return matchStatus;
    const s = inSearch.toLowerCase();
    return matchStatus && (item.filename?.toLowerCase().includes(s) || item.matter_ref?.toLowerCase().includes(s));
  });

  const filteredOut = outItems.filter(item => {
    const matchStatus = outStatusFilter === "all" || item.status === outStatusFilter;
    if (!outSearch) return matchStatus;
    const s = outSearch.toLowerCase();
    return matchStatus && (item.filename?.toLowerCase().includes(s) || item.matter_ref?.toLowerCase().includes(s));
  });

  // ── Combined summary counts ───────────────────────────────────────────────
  const totalPending = inCounts.pending + outCounts.pending;
  const totalFiled = inCounts.filed + outCounts.filed;
  const totalPartial = inCounts.partial;

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="gap-1">
                <ChevronLeft className="w-4 h-4" />Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-teal flex items-center gap-2">
                  <Inbox className="w-6 h-6" />Post Hub
                </h1>
                <p className="text-sm text-slate-500">Incoming & outgoing post — unified view</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === "incoming" ? (
                <Button onClick={handleInSync} disabled={inSyncing} className="gap-2 bg-teal hover:bg-teal/90">
                  <RefreshCw className={`w-4 h-4 ${inSyncing ? "animate-spin" : ""}`} />
                  {inSyncing ? "Scanning..." : "Sync Incoming"}
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setShowManualUpload(v => !v)} className="gap-1">
                    <Upload className="w-4 h-4" />{showManualUpload ? "Hide Upload" : "Upload Manually"}
                  </Button>
                  <Button onClick={handleOutSync} disabled={outSyncing} className="gap-2 bg-teal hover:bg-teal/90">
                    <RefreshCw className={`w-4 h-4 ${outSyncing ? "animate-spin" : ""}`} />
                    {outSyncing ? "Scanning..." : "Sync Outgoing"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Combined summary strip */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-md border bg-status-amber-dim border-amber-200">
            <p className="text-2xl font-bold text-status-amber">{totalPending}</p>
            <p className="text-sm text-slate-500">Total Pending</p>
            <p className="text-xs text-slate-400 mt-0.5">{inCounts.pending} in · {outCounts.pending} out</p>
          </div>
          <div className="p-4 rounded-md border bg-blue-50 border-blue-200">
            <p className="text-2xl font-bold text-blue-600">{totalPartial}</p>
            <p className="text-sm text-slate-500">Partially Done</p>
            <p className="text-xs text-slate-400 mt-0.5">Incoming only</p>
          </div>
          <div className="p-4 rounded-md border bg-teal-dim border-teal">
            <p className="text-2xl font-bold text-teal">{totalFiled}</p>
            <p className="text-sm text-slate-500">Filed</p>
            <p className="text-xs text-slate-400 mt-0.5">{inCounts.filed} in · {outCounts.filed} out</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab("incoming")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "incoming"
                ? "border-teal text-teal"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <ArrowDownToLine className="w-4 h-4" />Incoming Post
            {inCounts.pending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-status-amber-dim text-status-amber rounded-full">
                {inCounts.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("outgoing")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "outgoing"
                ? "border-teal text-teal"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <ArrowUpFromLine className="w-4 h-4" />Outgoing Post
            {outCounts.pending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-status-amber-dim text-status-amber rounded-full">
                {outCounts.pending}
              </span>
            )}
          </button>
        </div>

        {/* ── INCOMING TAB ─────────────────────────────────────────────────── */}
        {activeTab === "incoming" && (
          <div className="space-y-4">
            {/* Mini stat row */}
            <div className="grid grid-cols-3 gap-3">
              {["pending", "partial", "filed"].map(s => (
                <button
                  key={s}
                  onClick={() => setInStatusFilter(s)}
                  className={`p-3 rounded-md border text-left transition-colors ${
                    inStatusFilter === s
                      ? s === "pending" ? "bg-status-amber-dim border-amber-300"
                        : s === "partial" ? "bg-blue-50 border-blue-300"
                        : "bg-teal-dim border-teal"
                      : "bg-white border-navy-border hover:border-slate-300"
                  }`}
                >
                  <p className={`text-xl font-bold ${
                    s === "pending" ? "text-status-amber" : s === "partial" ? "text-blue-600" : "text-teal"
                  }`}>
                    {s === "pending" ? inCounts.pending : s === "partial" ? inCounts.partial : inCounts.filed}
                  </p>
                  <p className="text-xs text-slate-500">{IN_TAB_LABELS[s]}</p>
                </button>
              ))}
            </div>

            {/* Search + filter */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search filename or matter..." value={inSearch} onChange={e => setInSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={inStatusFilter} onValueChange={setInStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">{IN_TAB_LABELS.partial}</SelectItem>
                  <SelectItem value="filed">{IN_TAB_LABELS.filed}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchIncoming}><RefreshCw className="w-4 h-4" /></Button>
            </div>

            {/* List */}
            {inLoading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
                <p className="mt-4 text-slate-500">Loading incoming post...</p>
              </div>
            ) : filteredIn.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-md border border-dashed border-slate-300">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No incoming documents found</p>
                <p className="text-sm text-slate-400 mt-1">Click "Sync Incoming" to check for new scanned items</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredIn.map(item => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/post-intake/${item.id}`)}
                    className={`bg-white border rounded-md p-4 cursor-pointer transition-colors ${
                      item.status === "pending" ? "border-amber-200 hover:border-amber-300"
                      : item.status === "partial" ? "border-blue-200 hover:border-blue-300"
                      : "border-slate-200 hover:border-teal"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                          item.status === "filed" ? "bg-teal-dim" : item.status === "partial" ? "bg-blue-50" : "bg-status-amber-dim"
                        }`}>
                          {item.status === "filed"
                            ? <CheckCircle2 className="w-5 h-5 text-teal" />
                            : item.status === "partial"
                            ? <AlertCircle className="w-5 h-5 text-blue-500" />
                            : <FileText className="w-5 h-5 text-status-amber" />}
                        </div>
                        <div>
                          <p className="font-medium">{item.filename}</p>
                          <p className="text-sm text-slate-500">
                            {item.matter_ref ? `${item.matter_ref} · ` : ""}
                            Scanned: {fmt(item.created_at)}
                          </p>
                        </div>
                      </div>
                      <StatusPill status={item.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── OUTGOING TAB ─────────────────────────────────────────────────── */}
        {activeTab === "outgoing" && (
          <div className="space-y-4">
            {/* Mini stat row */}
            <div className="grid grid-cols-2 gap-3">
              {["pending", "filed"].map(s => (
                <button
                  key={s}
                  onClick={() => setOutStatusFilter(s)}
                  className={`p-3 rounded-md border text-left transition-colors ${
                    outStatusFilter === s
                      ? s === "pending" ? "bg-status-amber-dim border-amber-300" : "bg-teal-dim border-teal"
                      : "bg-white border-navy-border hover:border-slate-300"
                  }`}
                >
                  <p className={`text-xl font-bold ${s === "pending" ? "text-status-amber" : "text-teal"}`}>
                    {s === "pending" ? outCounts.pending : outCounts.filed}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">{s}</p>
                </button>
              ))}
            </div>

            {/* Search + filter */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search filename or matter..." value={outSearch} onChange={e => setOutSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={outStatusFilter} onValueChange={setOutStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="filed">Filed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { fetchOutgoing(); fetchOutgoingRecent(); }}><RefreshCw className="w-4 h-4" /></Button>
            </div>

            {/* List */}
            {outLoading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
                <p className="mt-4 text-slate-500">Loading outgoing post...</p>
              </div>
            ) : filteredOut.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-md border border-dashed border-slate-300">
                <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No outgoing documents found</p>
                <p className="text-sm text-slate-400 mt-1">Click "Sync Outgoing" or upload manually</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredOut.map(item => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/outgoing-post/${item.id}`)}
                    className={`bg-white border rounded-md p-4 cursor-pointer transition-colors ${
                      item.status === "pending" ? "border-amber-200 hover:border-amber-300" : "border-slate-200 hover:border-teal"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                          item.status === "filed" ? "bg-teal-dim" : "bg-status-amber-dim"
                        }`}>
                          {item.status === "filed"
                            ? <CheckCircle2 className="w-5 h-5 text-teal" />
                            : <FileText className="w-5 h-5 text-status-amber" />}
                        </div>
                        <div>
                          <p className="font-medium">{item.filename}</p>
                          <p className="text-sm text-slate-500">
                            {item.matter_ref ? `${item.matter_ref} · ` : ""}
                            Scanned: {fmt(item.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusPill status={item.status} />
                        {item.status === "pending" && (
                          assigningId === item.id ? (
                            <div className="relative w-64" onClick={e => e.stopPropagation()}>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input autoFocus value={assignMatterSearch} onChange={e => handleAssignMatterSearch(e.target.value)} placeholder="Search matter..." className="pl-10 h-8 text-sm" />
                                {assignSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 animate-spin" />}
                              </div>
                              <MatterSearchUnavailableNotice message={assignMatterError} />
                              {assignMatterResults.length > 0 && (
                                <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                                  {assignMatterResults.map(folder => (
                                    <button key={folder.id} onClick={() => handleAssignToMatter(item.id, folder.name)} className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0">
                                      <span className="font-medium">{folder.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              <Button variant="ghost" size="sm" className="absolute -right-8 top-0 h-8 w-8 p-0" onClick={() => { setAssigningId(null); setAssignMatterSearch(""); setAssignMatterResults([]); }}>
                                <XCircle className="w-4 h-4 text-slate-400" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="gap-1 border-teal text-teal hover:bg-teal/10" onClick={e => { e.stopPropagation(); setAssigningId(item.id); setAssignMatterSearch(""); setAssignMatterResults([]); }}>
                              <Search className="w-3 h-3" />Assign
                            </Button>
                          )
                        )}
                        {item.filed_sharepoint_url && (
                          <a href={item.filed_sharepoint_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-teal hover:underline">Open</a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Manual upload form */}
            {showManualUpload && (
              <div className="bg-white border border-navy-border rounded-md p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-teal" />Upload Manually
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">Matter Reference <span className="text-status-red">*</span></label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input value={outMatterSearch} onChange={e => handleMatterSearch(e.target.value)} placeholder="Search for a matter..." className="pl-10" />
                      {outMatterSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
                    </div>
                    <MatterSearchUnavailableNotice message={outMatterError} />
                    {outMatterResults.length > 0 && !outMatterRef && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {outMatterResults.map(folder => (
                          <button key={folder.id} onClick={() => selectMatter(folder)} className="w-full text-left px-4 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0">
                            <span className="font-medium">{folder.name}</span>
                            <span className="text-slate-400 ml-2 text-xs">{folder.childCount} items</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Document Type <span className="text-status-red">*</span></label>
                    <Select value={outDocType} onValueChange={setOutDocType}>
                      <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                      <SelectContent>{OUTGOING_DOC_TYPES.map(dt => <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description <span className="text-slate-400 text-xs">(optional)</span></label>
                    <Input value={outDescription} onChange={e => setOutDescription(e.target.value)} placeholder="e.g. Letter to Land Registry re Title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date Sent</label>
                    <Input type="date" value={outDateSent} onChange={e => setOutDateSent(e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">File <span className="text-status-red">*</span></label>
                    <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif" onChange={e => setOutFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal/10 file:text-teal hover:file:bg-teal/20" />
                    {outFile && <p className="text-xs text-slate-400 mt-1">{outFile.name} ({(outFile.size / 1024).toFixed(0)} KB)</p>}
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={handleOutgoingSubmit} disabled={outUploading || !outFile || !outDocType || !(outMatterRef || outMatterSearch.trim())} className="gap-2 bg-teal hover:bg-teal/90">
                    {outUploading ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload to SharePoint</>}
                  </Button>
                </div>
              </div>
            )}

            {/* Recent manual uploads */}
            {outRecentItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Recent Manual Uploads</h3>
                <div className="space-y-2">
                  {outRecentItems.map(item => (
                    <div key={item.id} className="bg-white border border-navy-border rounded-md p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                            <Send className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium">{item.original_filename}</p>
                            <p className="text-sm text-slate-500">
                              {item.matter_ref} · {OUTGOING_DOC_TYPES.find(t => t.value === item.document_type)?.label || item.document_type}
                              {item.description ? ` — ${item.description}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">Sent: {item.date_sent}</span>
                          <StatusPill status={item.sharepoint_url ? "filed" : "pending"} />
                          {item.sharepoint_url && <a href={item.sharepoint_url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal hover:underline">Open</a>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
